import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, MenuList, SwipeableDrawer } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ToolbarActionMenuState {
  closeMenu: () => void;
}

const ToolbarActionMenuContext = createContext<ToolbarActionMenuState | undefined>(undefined);

/**
 * Returns the surrounding toolbar action menu, or undefined when rendered outside of one.
 * Used by LinkIconButton to render as a MenuItem inside the menu.
 */
export function useToolbarActionMenu(): ToolbarActionMenuState | undefined {
  return useContext(ToolbarActionMenuContext);
}

/**
 * Collapses toolbar action buttons into a single overflow button (⋮) that opens a
 * bottom sheet. LinkIconButtons rendered as children automatically become menu items
 * with their title as label.
 */
export const ToolbarActionMenu: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const contextValue = useMemo(() => ({ closeMenu }), [closeMenu]);

  return (
    <>
      <IconButton data-test-id="toolbar-action-menu-button" onClick={handleOpen} title={t('common:button.more')}>
        <MoreVertIcon />
      </IconButton>
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onOpen={handleOpen}
        onClose={closeMenu}
        disableSwipeToOpen
        ModalProps={{ keepMounted: true }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 455,
              margin: 'auto',
              py: 1,
            },
          },
        }}
      >
        <Box sx={{ width: 56, height: 4, borderRadius: 80, backgroundColor: 'text.disabled', mx: 'auto', mb: 1 }} />
        <MenuList sx={{ '& .MuiMenuItem-root': { paddingY: 0.75 } }}>
          <ToolbarActionMenuContext.Provider value={contextValue}>{children}</ToolbarActionMenuContext.Provider>
        </MenuList>
      </SwipeableDrawer>
    </>
  );
};
